// /api/shop calls (the existing shop routes; same users and sessions as the legacy site).
// Contract: platform/docs/api-inventory/content-ops.md section 3 and payments.md section 1.8.
// Prices, channels and ownership always come from the server; the client never sends an amount.
import type { Call } from './call'

/** Version string every order must echo; read it from the product detail or the agreement. */
export type WebPayChannel = 'WECHAT_NATIVE' | 'WECHAT_H5' | 'ALIPAY_PAGE' | 'ALIPAY_WAP'
export type ShopProvider = 'WECHAT' | 'ALIPAY'
export type ShopProductType = 'IMAGE_PACK' | 'MOCK_EXAM'
export type ShopOrderStatus = 'pending' | 'paid' | 'expired' | 'cancelled' | 'refunded' | (string & {})

export interface ShopProduct {
  id: string
  title: string
  type: ShopProductType | (string & {})
  amountCents: number
  currency: string
  description: string
  cover: string
  samples: string[]
  year?: number
  version?: number
  available: boolean
  owned: boolean
  channels: string[]
  deliverySummary: string
  expiresAt: string | null
  durationDays: number | null
  subjects: number[]
  appleProductId?: string | null
}

export interface ShopAsset {
  id: string
  deliveryType: 'OSS_PRIVATE' | 'LOCAL_PRIVATE' | 'QUARK_SHARE' | (string & {})
  title: string
  filename?: string
  bytes?: number
  sha256?: string
  subject?: number | null
}

export interface ShopPaperPractice {
  sessionId: string | null
  currentIndex: number
  questionCount: number
  completedCount: number
  status: 'not_started' | 'in_progress' | 'completed' | (string & {})
}

export interface ShopPaper {
  id: string
  title: string
  setNo: number | null
  paperNo: number | null
  subjects: number[]
  durationSeconds: number
  durationMinutes: number
  questionCount: number
  totalScore: number
  questionIds: string[]
  practice?: ShopPaperPractice
}

export interface ShopMembershipGift {
  status: 'not_purchased' | 'pending' | 'granted' | 'revoked' | 'not_included' | (string & {})
  days: number
  activeUntil?: string | null
}

export interface ShopProductDetail {
  product: ShopProduct
  assets: ShopAsset[]
  papers: ShopPaper[]
  agreementVersion: string
  overlapSubjects: number[]
  membershipGift?: ShopMembershipGift
}

export interface ShopAgreement {
  version: string
  title: string
  content: string
}

export interface ShopOrder {
  orderId: string
  merchantOrderId?: string
  productId: string
  title: string
  amountCents: number
  currency: string
  provider: string
  channels: string[]
  status: ShopOrderStatus
  createdAt: string
  expiresAt: string
  giftDays?: number
  agreementVersion?: string
}

export interface ShopLibrary {
  products: ShopProduct[]
  orders: ShopOrder[]
}

export interface CreateShopOrderInput {
  productId: string
  provider: ShopProvider
  /** 8-128 characters of [-A-Za-z0-9._:]; resending the same key returns the same order. */
  idempotencyKey: string
  agreementVersion: string
}

export type ShopPayment =
  | { codeUrl: string }
  | { h5Url: string }
  | { method: 'POST'; action: string; fields: Record<string, string> }

export type ShopDownload =
  | { deliveryType: 'QUARK_SHARE'; title?: string; shareUrl: string; extractionCode?: string }
  | { deliveryType: string; title?: string; filename: string; bytes?: number; sha256?: string; url: string; expiresAt?: string }

export interface ShopAttemptQuestion {
  id: string
  stem: string
  options: string[]
  type: 'single' | 'multiple' | 'judge' | (string & {})
  points: number
}

export interface ShopAttemptAnswer {
  questionId: string
  /** Option letters, "A", "B", ... */
  selected: string[]
}

export interface ShopAttemptResult {
  questionId: string
  selected: string[]
  correct: boolean
  correctAnswers: string[]
  explanation: string
  points: number
}

export interface ShopAttempt {
  id: string
  paperId: string
  title: string
  version: number
  status: 'active' | 'submitted' | (string & {})
  startedAt: string
  deadlineAt: string
  serverNow: string
  questions: ShopAttemptQuestion[]
  answers: ShopAttemptAnswer[]
  score?: number
  results?: ShopAttemptResult[]
  submittedAt?: string
}

export interface SprintQuestion {
  id: string
  stem: string
  options: string[]
  type: string
}

export interface SprintResult {
  questionId: string
  selected: string[]
  isCorrect: boolean
  correctAnswer: string[] | string
  explanation: string
}

export interface SprintSession {
  id: string
  paperId: string
  version: number
  currentIndex: number
  currentQuestionId: string | null
  questionIds: string[]
  completedQuestionIds: string[]
  answers: SprintResult[]
  favorites: string[]
  updatedAt?: string
  questions: SprintQuestion[]
}

const enc = encodeURIComponent

export function createShopApi(call: Call) {
  return {
    agreement: () => call<ShopAgreement>({ method: 'GET', path: '/api/shop/agreement' }),
    products: () => call<{ products: ShopProduct[] }>({ method: 'GET', path: '/api/shop/products' }),
    product: (id: string) => call<ShopProductDetail>({ method: 'GET', path: `/api/shop/products/${enc(id)}` }),
    library: () => call<ShopLibrary>({ method: 'GET', path: '/api/shop/library' }),

    createOrder: (input: CreateShopOrderInput) =>
      call<{ order: ShopOrder }>({
        method: 'POST',
        path: '/api/shop/orders',
        body: { ...input, agreementAccepted: true },
      }),
    order: (orderId: string) => call<{ order: ShopOrder }>({ method: 'GET', path: `/api/shop/orders/${enc(orderId)}` }),
    startPayment: (orderId: string, channel: WebPayChannel) =>
      call<{ payment: ShopPayment }>({ method: 'POST', path: `/api/shop/orders/${enc(orderId)}/payment`, body: { channel } }),

    /** Signed or authorized delivery for an owned asset: a download URL or a Quark share. */
    download: (assetId: string) => call<ShopDownload>({ method: 'POST', path: `/api/shop/downloads/${enc(assetId)}`, body: {} }),

    startAttempt: (paperId: string) =>
      call<{ attempt: ShopAttempt }>({ method: 'POST', path: `/api/shop/papers/${enc(paperId)}/attempts`, body: {} }),
    attempt: (attemptId: string) => call<{ attempt: ShopAttempt }>({ method: 'GET', path: `/api/shop/attempts/${enc(attemptId)}` }),
    saveAttempt: (attemptId: string, version: number, answers: ShopAttemptAnswer[]) =>
      call<{ attempt: ShopAttempt }>({ method: 'PUT', path: `/api/shop/attempts/${enc(attemptId)}`, body: { version, answers } }),
    submitAttempt: (attemptId: string, version: number, answers: ShopAttemptAnswer[]) =>
      call<{ attempt: ShopAttempt }>({ method: 'POST', path: `/api/shop/attempts/${enc(attemptId)}/submit`, body: { version, answers } }),

    startPractice: (paperId: string, restart = false) =>
      call<{ session: SprintSession }>({
        method: 'POST',
        path: `/api/shop/papers/${enc(paperId)}/practice`,
        body: restart ? { restart: true } : {},
      }),
    practice: (sessionId: string) => call<{ session: SprintSession }>({ method: 'GET', path: `/api/shop/practice/${enc(sessionId)}` }),
    savePractice: (sessionId: string, input: { version: number; currentIndex?: number; favorites?: string[] }) =>
      call<{ session: SprintSession }>({ method: 'PUT', path: `/api/shop/practice/${enc(sessionId)}`, body: input }),
    answerPractice: (sessionId: string, input: { version: number; questionId: string; selected: string[]; idempotencyKey: string }) =>
      call<{ result: SprintResult; session: SprintSession }>({
        method: 'POST',
        path: `/api/shop/practice/${enc(sessionId)}/answers`,
        body: input,
      }),
  }
}

export type ShopApi = ReturnType<typeof createShopApi>
